import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';
import SearchIcon from '@mui/icons-material/Search';
import TodayIcon from '@mui/icons-material/Today';
import {
    Box,
    Chip,
    FormControl,
    Grid,
    InputAdornment,
    Paper,
    Stack,
    styled,
    TextField,
    Theme,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import {
    DataGridPro,
    GridColDef,
    GridColumnGroupHeaderParams,
    GridColumnGroupingModel,
    GridColumnVisibilityModel,
    GridPaginationModel,
    GridSortModel,
} from '@mui/x-data-grid-pro';
import { DateRange } from '@mui/x-date-pickers-pro';
import dinero from 'dinero.js';
import moment, { Moment } from 'moment';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SextforceMetricsCampaignsClaimedTodayOverview } from '../../../../../../hooks/useSextforceMetricsCampaignsClaimedToday';
import { OnlyFansMetrics } from '../../../../../../hooks/useSextforceMetricsCampaignsOverview';
import { Platform, SettingsContext } from '../../../../../../store/SettingsContext';
import { OnlyFansLists } from '../../../../../../types/onlyFansTypes';
import { d2f, getPlatformName, metricTypeName } from '../../../../../../utils/common';
import DataGridColumnHeaderCell from '../../../../../common/DataGridColumnHeaderCell';
import DateRangeSelect from '../../../../../common/DateRangeSelect';
import DataGridColumnVisibilityDialog from '../../DataGridColumnVisibilityDialog';
import SextforceMetricsClaimed7DaysGraph from '../../SextforceMetricsClaimed7DaysGraph';
import SextforceMetricsClaimedTodayChip from '../../SextforceMetricsClaimedTodayChip';
import SextforceMetricsClaimsCountChip from '../../SextforceMetricsClaimsCountChip';
import SextforceMetricsClaimsTodayDateSelectorDialog from '../../SextforceMetricsClaimsTodayDateSelectorDialog';
import SextforceCrossReferenceEarningsLabel from '../../SextforceMetricsCrossReferenceEarningsLabel';
import SextforceMetricsEarrnigsTotalChip from '../../SextforceMetricsEarningsTotalChip';
import SextforceMetricsEditSettingsDialog from '../../SextforceMetricsEditSettingsDialog';
import SextforceMetricsRecentEarchingsChip from '../../SextforceMetricsRecentEarningsChip';
import SextforceMetricsROIAmountChip from '../../SextforceMetricsROIAmountChip';
import SextforceMetricsROIPercentageChip from '../../SextforceMetricsROIPercentageChip';
import SextforceMetricsShareDialog from '../../SextforceMetricsShareDialog';
import SextforceMetricsTrialsWelcomeMessageDialog from '../SextforceMetricsTrialsWelcomeMessageDialog';
import { OnlyFansMetricsTrialsPaginated } from './SextforceMetricsTrialsOverview';
import SextforceMetricsTrialsOverviewGridLocalMenu from './SextforceMetricsTrialsOverviewGridLocalMenu';

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    '& .dataGrid-row-highlight': {
        // Create a left hand side background color bar using a gradient for the highlighted row
        backgroundColor: theme.palette.primary.light,
        backgroundImage: `linear-gradient(to right, ${theme.palette.primary.light} 15%, ${theme.palette.common.white} 15%)`,

        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center left',
    },
    '& .dataGrid-row-normal': {
        backgroundColor: theme.palette.common.white,
    },
}));

interface Props {
    subscriber: any;
    metrics: OnlyFansMetricsTrialsPaginated | undefined;
    metricsType: string;
    amountType: 'gross' | 'net';
    isLoading: boolean;
    saveSetMetricCompleted: (metricId: string, completed: boolean, callBack: () => void) => void;
    saveSettings: (
        trialId: string,
        platform: string,
        associatedUsername: string,
        goalSpent: number,
        goalSubs: number,
        addUsersToListId: { id: number | string | OnlyFansLists.Type; name: string } | null | undefined,
        callBack: () => void,
    ) => void;
    isSavingSettings: boolean;
    reportSort: GridSortModel;
    setReportSort: (model: GridSortModel) => void;
    // filter: GridFilterModel;
    // setFilter: (model: GridFilterModel) => void;
    search: string;
    setSearch: (search: string) => void;
    searchDateRange: DateRange<Moment>;
    setSearchDateRange: (dateRange: DateRange<Moment>) => void;
    paginationModel: GridPaginationModel;
    setPaginationModel: (model: GridPaginationModel) => void;
    startDate: Date;
    endDate: Date;
    claimedTodayDate: Date | null;
    setClaimedTodayDate: (date: Date | null) => void;
    timezone: string;
    setTimezone: (timezone: string) => void;
    crossReferenceSubscriberId: string;
    hoverMetricId: string;
    claimsTodayOverview: SextforceMetricsCampaignsClaimedTodayOverview[] | undefined;
    claimsTodayOverviewLoading: boolean;
}

const ColumnGroupHeaderCell = (params: GridColumnGroupHeaderParams) => (
    <div
        style={{
            fontWeight: 'bold',
        }}
    >
        {params.groupId}
    </div>
);

const columnGroupingModel = (metricsType: string): GridColumnGroupingModel => [
    {
        groupId: 'Metric',
        headerAlign: 'center',
        children: [
            { field: 'foreignId' },
            ...(metricsType === 'all' ? [{ field: 'type' }] : []),
            { field: 'payload.active' },
            { field: 'payload.createdAt' },
            { field: 'payload.name' },
            { field: 'settings.addUsersToList', headerAlign: 'center' },
        ],
        renderHeaderGroup: params => ColumnGroupHeaderCell(params),
    },
    {
        groupId: 'Bought From / Advertised On',
        headerAlign: 'center',
        children: [{ field: 'settings.platform' }, { field: 'associatedUsername' }],
        renderHeaderGroup: params => ColumnGroupHeaderCell(params),
    },
    {
        groupId: 'Claims',
        headerAlign: 'center',
        children: [
            { field: 'claimedGraph' },
            { field: 'claimedToday' },
            { field: 'payload.counters.claimsCount' },
            { field: 'settings.goalSubs' },
        ],
        renderHeaderGroup: params => ColumnGroupHeaderCell(params),
    },
    {
        groupId: 'Earnings',
        headerAlign: 'center',
        children: [
            { field: 'earningsToday' },
            { field: 'payload.cpf' },
            { field: 'payload.averageTotalPerSpender' },
            { field: 'payload.earningsTotal' },
            { field: 'settings.goalSpent' },
            { field: 'roi.amount' },
            { field: 'roi.percentage' },
            { field: 'crossReferenceEarnings' },
            { field: 'totalWithCrossReferenceEarnings' },
        ],
        renderHeaderGroup: params => ColumnGroupHeaderCell(params),
    },
];

const columns = (
    searchParams: any,
    amountType: 'gross' | 'net',
    openEditDialog: (trialId: string) => void,
    openWelcomeMessageDialog: (trialId: string) => void,
    openShareDialog: (trialId: string) => void,
    saveSetMetricCompleted: (metricId: string, completed: boolean, callBack: () => void) => void,
    openClaimedTodayDateDialog: () => void,
    platforms: { [name: string]: Platform },
    metricsType: string,
    crossReferenceSubscriberId: string,
    startDate: Date,
    endDate: Date,
    timezone: string,
    hoverMetricId: string,
    theme: Theme,
    claimsTodayOverview: SextforceMetricsCampaignsClaimedTodayOverview[] | undefined,
    claimsTodayOverviewLoading: boolean,
    isLargeScreen: boolean,
): GridColDef[] => {
    return [
        {
            field: 'foreignId',
            headerName: 'ID',
            width: 100,
            align: 'right',
            headerAlign: 'right',
            cellClassName: (params: any) => (params.row._id === hoverMetricId ? 'dataGrid-row-highlight' : 'dataGrid-row-normal'),
            renderCell: params => (
                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between">
                    <Tooltip
                        title={
                            params.row.payload.completedAt
                                ? `Completed on: ${moment(params.row.payload.completedAt).format('L LT')}`
                                : 'Not completed'
                        }
                        placement="top"
                        arrow
                    >
                        <DoneIcon fontSize="small" color={params.row.payload.completed ? 'success' : 'disabled'} />
                    </Tooltip>
                    <Link
                        to={`/subscribers/${searchParams.userId}/services/sextforce/metrics/trials/details/${params.row._id}?type=${
                            params.row.type
                        }${params.row.payload && !params.row.payload.active ? '&includeInactive=true' : ''}`}
                        style={{ fontFamily: 'monospace', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                    >
                        {params.value}
                    </Link>
                </Stack>
            ),
            renderHeader: params => DataGridColumnHeaderCell(params),
        },
        ...(metricsType === 'all'
            ? [
                  {
                      field: 'type',
                      headerName: 'Type',
                      width: 80,
                      align: 'center',
                      headerAlign: 'center',
                      renderCell: params => (
                          <div style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>{metricTypeName(params.value)}</div>
                      ),
                      renderHeader: params => DataGridColumnHeaderCell(params),
                  } as GridColDef<any, any, any>,
              ]
            : []),
        {
            field: 'payload.active',
            headerName: 'Active',
            width: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: params => (params.row.payload.active === true ? 'Yes' : 'No'),
            renderHeader: params => DataGridColumnHeaderCell(params),
        },
        {
            field: 'payload.createdAt',
            headerName: 'Created At',
            width: 130,
            headerAlign: 'right',
            align: 'right',
            valueGetter: params => (params.row.payload && params.row.payload.createdAt) || null,
            renderCell: params =>
                params.row.payload &&
                params.row.payload.createdAt && (
                    <div
                        title={params.value ? moment(params.value).format('L LT') : ''}
                        style={{ fontFamily: 'monospace', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                    >
                        {params.value ? moment(params.value).format('L') : ''}
                    </div>
                ),
            sortComparator: (v1: Date, v2: Date) => (v1 && v2 ? (v1.valueOf() > v2.valueOf() ? 1 : -1) : 0),
            renderHeader: params => DataGridColumnHeaderCell(params),
        },
        {
            field: 'payload.name',
            headerName: 'OnlyFans Name',
            flex: 1,
            minWidth: 200,
            valueGetter: params =>
                params.row.payload && params.row.payload.name && params.row.payload.name.length > 0 ? params.row.payload.name : 'No Name',
            renderCell: params => (
                <div title={params.row.payload.name} style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {params.row.payload.name || 'No Name'}
                </div>
            ),
            renderHeader: params => DataGridColumnHeaderCell(params),
        },
        {
            field: 'settings.addUsersToList',
            headerName: 'List',
            flex: 1,
            minWidth: 70,
            align: 'center',
            sortable: false,
            valueGetter: params => params.row.settings?.addUsersToList || null,
            renderCell: params => (
                <Tooltip title={params.value ? `List: ${params.value.name}` : 'No list assigned'}>
                    <Chip
                        label={params.value ? 'Auto' : 'None'}
                        size="small"
                        color={params.value ? 'primary' : 'default'}
                        onClick={() => {
                            openEditDialog(params.row._id);
                        }}
                        sx={{
                            cursor: 'pointer',
                            backgroundColor: params.value ? theme.palette.primary.light : undefined,
                            '&:hover': {
                                backgroundColor: params.value ? theme.palette.primary.main : undefined,
                            },
                        }}
                    />
                </Tooltip>
            ),
            renderHeader: params => DataGridColumnHeaderCell(params),
        },
        {
            field: 'settings.platform',
            headerName: 'Platform',
            width: 120,
            renderCell: params => (
                <div style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {params.row.settings && params.row.settings.platform ? getPlatformName(platforms, params.row.settings.platform) : ''}
                </div>
            ),
            renderHeader: params => DataGridColumnHeaderCell(params),
        },
        {
            field: 'associatedUsername',
            headerName: '@',
            width: 150,
            renderCell: params => <div style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>{params.row.associatedUsername || ''}</div>,
            renderHeader: params => DataGridColumnHeaderCell(params),
        },
        {
            field: 'claimedToday',
            headerName: 'Today',
            width: 90,
            align: 'right',
            headerAlign: 'center',
            renderCell: params => (
                <SextforceMetricsClaimedTodayChip
                    metricId={params.row._id!}
                    metricType={params.row.type}
                    startDate={startDate}
                    endDate={endDate}
                    timezone={timezone}
                />
            ),
            renderHeader: params => (
                <>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {DataGridColumnHeaderCell(params)}
                        <Tooltip title="Change the date to see how many fans claimed a promo on this day">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        openClaimedTodayDateDialog();
                                    }}
                                >
                                    <TodayIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </>
            ),
            sortComparator: (v1: any, v2: any) => (v1 > v2 ? 1 : -1),
            sortable: false,
        },
        {
            field: 'claimedGraph',
            headerName: 'Last 7 Days',
            width: 140,
            align: 'center',
            headerAlign: 'center',
            renderCell: params => (
                <SextforceMetricsClaimed7DaysGraph
                    metricId={params.row._id!}
                    claimsTodayOverview={claimsTodayOverview}
                    claimsTodayOverviewLoading={claimsTodayOverviewLoading}
                />
            ),
            renderHeader: params => DataGridColumnHeaderCell(params),
            sortComparator: (v1: any, v2: any) => (v1 > v2 ? 1 : -1),
            sortable: false,
        },
        {
            field: 'payload.counters.claimsCount',
            headerName: 'Total',
            width: 110,
            align: 'right',
            headerAlign: 'center',
            valueGetter: params => (params.row.payload && params.row.payload.counters && params.row.payload.counters.claimsCount) || 0,
            renderCell: params => <SextforceMetricsClaimsCountChip params={params} />,
            renderHeader: params => DataGridColumnHeaderCell(params),
            sortComparator: (v1: any, v2: any) => (v1 > v2 ? 1 : -1),
            sortable: true,
        },
        {
            field: 'settings.goalSubs',
            headerName: 'Goal',
            width: 90,
            align: 'right',
            headerAlign: 'center',
            valueGetter: params => params.row.settings && params.row.settings.goalSubs,
            renderCell: params => {
                return (
                    params.value && (
                        <div style={{ fontFamily: 'monospace', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                            {params.value.toLocaleString()}
                        </div>
                    )
                );
            },
            renderHeader: params => DataGridColumnHeaderCell(params),
            sortComparator: (v1: any, v2: any) => (v1 > v2 ? 1 : -1),
        },
        {
            field: 'earningsToday',
            headerName: `Last 24H  ${amountType === 'gross' ? 'Gross' : 'Net'}`,
            width: 120,
            align: 'right',
            headerAlign: 'center',
            renderCell: params => (
                <SextforceMetricsRecentEarchingsChip
                    subscriberId={params.row.subscriberId}
                    metricId={params.row._id}
                    metricType={params.row.type}
                    amountType={amountType}
                />
            ),
            renderHeader: params => DataGridColumnHeaderCell(params),
            sortComparator: (v1: any, v2: any) => (v1 > v2 ? 1 : -1),
            sortable: false,
        },
        {
            field: 'payload.cpf',
            headerName: `CPF Net`,
            width: 130,
            headerAlign: 'center',
            valueGetter: params =>
                params.row.payload && params.row.payload.cpf && typeof params.row.payload.cpf === 'number'
                    ? (params.row.payload.cpf as number)
                    : 0,
            renderCell: params => (
                <Grid container alignItems="center" justifyItems="flex-end" flexGrow={1}>
                    <Grid
                        item
                        xs={12}
                        sx={{
                            fontFamily: 'monospace',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            textAlign: 'right',
                        }}
                    >
                        {dinero({
                            amount: Math.trunc(params.value * 100),
                            currency: 'USD',
                        }).toFormat()}
                    </Grid>
                </Grid>
            ),
            renderHeader: params => DataGridColumnHeaderCell(params, 'Cost Per Fan (net)'),
            sortComparator: (v1: any, v2: any) => (v1 > v2 ? 1 : -1),
            sortable: true,
        },
        {
            field: 'payload.averageTotalPerSpender',
            headerName: `ASPF ${amountType === 'gross' ? 'Gross' : 'Net'}`,
            width: 140,
            headerAlign: 'center',
            valueGetter: params =>
                params.row.payload && params.row.payload.averageTotalPerSpender
                    ? d2f(params.row.payload.averageTotalPerSpender) * (amountType === 'gross' ? 1 : 0.8)
                    : 0,
            renderCell: params => (
                <Grid container alignItems="center" justifyItems="flex-end" flexGrow={1}>
                    <Grid
                        item
                        xs={12}
                        sx={{
                            fontFamily: 'monospace',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            textAlign: 'right',
                        }}
                    >
                        {dinero({
                            amount: Math.trunc(params.value * 100),
                            currency: 'USD',
                        }).toFormat()}
                    </Grid>
                </Grid>
            ),
            renderHeader: params => DataGridColumnHeaderCell(params, 'Average Spending Per Fan'),
            sortComparator: (v1: any, v2: any) => (v1 > v2 ? 1 : -1),
            sortable: true,
        },
        {
            field: 'payload.earningsTotal',
            headerName: `Total ${amountType === 'gross' ? 'Gross' : 'Net'}`,
            width: 140,
            align: 'right',
            headerAlign: 'center',
            valueGetter: params =>
                params.row.payload && params.row.payload.earningsTotal
                    ? d2f(params.row.payload.earningsTotal) * (amountType === 'gross' ? 1 : 0.8)
                    : 0,
            renderCell: params => <SextforceMetricsEarrnigsTotalChip params={params} />,
            renderHeader: params => DataGridColumnHeaderCell(params),
            sortComparator: (v1: any, v2: any) => (v1 > v2 ? 1 : -1),
            sortable: true,
        },
        {
            field: 'settings.goalSpent',
            headerName: `Goal ${amountType === 'gross' ? 'Gross' : 'Net'}`,
            width: 120,
            align: 'right',
            headerAlign: 'center',
            valueGetter: params => (params.row.settings && params.row.settings.goalSpent ? d2f(params.row.settings.goalSpent) : undefined),
            renderCell: params => {
                return (
                    params.value && (
                        <div style={{ fontFamily: 'monospace', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                            {dinero({
                                amount: Math.trunc(params.value * 100 * (amountType === 'gross' ? 1 : 0.8)),
                                currency: 'USD',
                            }).toFormat()}
                        </div>
                    )
                );
            },
            renderHeader: params => DataGridColumnHeaderCell(params),
            sortComparator: (v1: any, v2: any) => (v1 > v2 ? 1 : -1),
        },
        {
            field: 'roi.amount',
            headerName: `ROI $ Net`,
            width: 140,
            align: 'right',
            headerAlign: 'center',
            valueGetter: params => (params.row.roi && params.row.roi.amount ? d2f(params.row.roi.amount) : undefined),
            renderCell: params => SextforceMetricsROIAmountChip({ amount: params.value, amountType }),
            renderHeader: params => DataGridColumnHeaderCell(params, 'The earnings minus the goal/cost of the campaign'),
            sortComparator: (v1: any, v2: any) => (v1 > v2 ? 1 : -1),
        },
        {
            field: 'roi.percentage',
            headerName: `ROI % Net`,
            width: 140,
            align: 'right',
            headerAlign: 'center',
            valueGetter: params => (params.row.roi && params.row.roi.percentage ? d2f(params.row.roi.percentage) : undefined),
            renderCell: params => SextforceMetricsROIPercentageChip({ value: params.value, amountType }),
            renderHeader: params => DataGridColumnHeaderCell(params, 'The earnings minus the goal/cost of the campaign as a percentage'),
            sortComparator: (v1: any, v2: any) => (v1 > v2 ? 1 : -1),
        },
        {
            field: 'crossReferenceEarnings',
            headerName: 'Cross-Poll. Earnings',
            width: 150,
            align: 'right',
            headerAlign: 'center',
            renderCell: params =>
                params.row.type === 'trialLinkTrial' ? (
                    <SextforceCrossReferenceEarningsLabel
                        metricId={params.row._id}
                        type="trial"
                        crossReferenceSubscriberId={crossReferenceSubscriberId}
                        displayMode={amountType}
                    />
                ) : (
                    <></>
                ),
            renderHeader: params => DataGridColumnHeaderCell(params),
            sortable: false,
        },
        {
            field: 'totalWithCrossReferenceEarnings',
            headerName: 'Total w/Cross-Poll.',
            width: 150,
            align: 'right',
            headerAlign: 'center',
            renderCell: params => (
                <SextforceCrossReferenceEarningsLabel
                    metricId={params.row._id}
                    type="campaign"
                    crossReferenceSubscriberId={crossReferenceSubscriberId}
                    displayMode={amountType}
                    addAmount={
                        params.row.payload && params.row.payload.earningsTotal
                            ? d2f(params.row.payload.earningsTotal) * (amountType === 'gross' ? 1 : 0.8)
                            : 0
                    }
                />
            ),
            renderHeader: params => DataGridColumnHeaderCell(params),
            sortable: false,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 60,
            sortable: false,
            disableColumnMenu: true,
            headerAlign: 'center',
            align: 'center',
            renderCell: params => {
                return (
                    <SextforceMetricsTrialsOverviewGridLocalMenu
                        params={params}
                        openEditDialog={openEditDialog}
                        openWelcomeMessageDialog={openWelcomeMessageDialog}
                        openShareDialog={openShareDialog}
                        saveSetMetricCompleted={saveSetMetricCompleted}
                    />
                );
            },
            renderHeader: params => DataGridColumnHeaderCell(params),
        },
    ];
};

const SextforceMetricsTrialsOverviewGrid: React.FC<Props> = (props: Props) => {
    const {
        subscriber,
        metrics,
        metricsType,
        amountType,
        isLoading,
        saveSetMetricCompleted,
        saveSettings,
        isSavingSettings,
        reportSort,
        setReportSort,
        // filter,
        // setFilter,
        search,
        setSearch,
        searchDateRange,
        setSearchDateRange,
        paginationModel,
        setPaginationModel,
        startDate,
        endDate,
        claimedTodayDate,
        setClaimedTodayDate,
        timezone,
        setTimezone,
        crossReferenceSubscriberId,
        hoverMetricId,
        claimsTodayOverview,
        claimsTodayOverviewLoading,
    } = props;
    const theme: Theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('sm'));
    const params = useParams();
    const settings = useContext(SettingsContext);

    // Dialogs
    const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
    const [editMetric, setEditMetric] = useState<OnlyFansMetrics | null>(null);
    const [welcomeMessageDialogOpen, setWelcomeMessageDialogOpen] = useState<boolean>(false);
    const [welcomeMessageMetric, setWelcomeMessageMetric] = useState<OnlyFansMetrics | null>(null);
    const [shareDialogOpen, setShareDialogOpen] = useState<boolean>(false);
    const [shareMetric, setShareMetric] = useState<OnlyFansMetrics | null>(null);
    const [claimedTodayDateDialogOpen, setClaimedTodayDateDialogOpen] = useState<boolean>(false);

    // Add state for column visibility
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

    const openEditDialog = (trialId: string) => {
        if (!metrics || !metrics.data) {
            return;
        }

        const foundMetricData = metrics.data.find(trial => trial._id === trialId);

        if (foundMetricData) {
            setEditDialogOpen(true);
            setEditMetric(foundMetricData);
        }
    };

    const openWelcomeMessageDialog = (trialId: string) => {
        if (!metrics || !metrics.data) {
            return;
        }

        const foundMetricData = metrics.data.find(trial => trial._id === trialId);

        if (foundMetricData) {
            setWelcomeMessageDialogOpen(true);
            setWelcomeMessageMetric(foundMetricData);
        }
    };

    const openShareDialog = (trialId: string) => {
        if (!metrics || !metrics.data) {
            return;
        }

        const foundMetricData = metrics.data.find(trial => trial._id === trialId);

        if (foundMetricData) {
            setShareDialogOpen(true);
            setShareMetric(foundMetricData);
        }
    };

    const openClaimedTodayDateDialog = () => {
        setClaimedTodayDateDialogOpen(true);
    };

    const handleShareDialogClose = () => {
        setShareDialogOpen(false);
        setShareMetric(null);
    };

    const [searchFieldValue, setSearchFieldValue] = useState<string>('');
    const [dateRangeValue, setDateRangeValue] = useState<string>('alltime');

    // Wait 100ms before updating search after searchFieldValue has been updated
    useEffect(() => {
        const timeout = setTimeout(() => {
            setSearch(searchFieldValue);
        }, 400);

        return () => {
            clearTimeout(timeout);
        };
    }, [searchFieldValue, setSearch]);

    // Memoize the columns
    const memoizedColumns = useMemo(
        () =>
            columns(
                params,
                amountType,
                openEditDialog,
                openWelcomeMessageDialog,
                openShareDialog,
                saveSetMetricCompleted,
                openClaimedTodayDateDialog,
                settings.platforms,
                metricsType,
                crossReferenceSubscriberId,
                startDate,
                endDate,
                timezone,
                hoverMetricId,
                theme,
                claimsTodayOverview,
                claimsTodayOverviewLoading,
                isLargeScreen,
            ),
        [
            params,
            amountType,
            settings.platforms,
            metricsType,
            crossReferenceSubscriberId,
            startDate,
            endDate,
            timezone,
            hoverMetricId,
            theme,
            claimsTodayOverview,
            claimsTodayOverviewLoading,
            isLargeScreen,
        ],
    );

    // Handle column visibility model changes
    const handleColumnVisibilityModelChange = (newModel: GridColumnVisibilityModel): void => {
        setColumnVisibilityModel(newModel);
    };

    const groupingModel = columnGroupingModel(metricsType);

    return (
        <>
            <Box sx={{ padding: 0, marginBottom: 1, boxShadow: 'none' }}>
                <Grid container spacing={1} alignItems={'center'}>
                    <Grid item xs>
                        <FormControl fullWidth variant="outlined">
                            <TextField
                                fullWidth
                                label="Search"
                                value={searchFieldValue}
                                onChange={e => {
                                    setSearchFieldValue(e.currentTarget.value);
                                }}
                                sx={{
                                    margin: 0,
                                    bgcolor: 'background.paper',
                                }}
                                onKeyDown={e => {
                                    if (e.key === 'Escape') {
                                        setSearchFieldValue('');
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: search.length > 0 && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => {
                                                    setSearchFieldValue('');
                                                }}
                                            >
                                                <ClearIcon color="error" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs="auto">
                        <DateRangeSelect
                            limitStartDate={subscriber?.ofAccountStartDate ? moment(subscriber.ofAccountStartDate) : undefined}
                            limitEndDate={subscriber?.ofAccountStartDate ? moment() : undefined}
                            dateRange={searchDateRange}
                            value={dateRangeValue}
                            onChange={(value: string, range?: DateRange<Moment>) => {
                                setDateRangeValue(value);

                                if (!range) {
                                    return;
                                }
                                setSearchDateRange(range);
                            }}
                            label="Dates"
                        />
                    </Grid>
                    <Grid item xs="auto">
                        <DataGridColumnVisibilityDialog
                            columns={memoizedColumns}
                            columnGroupingModel={groupingModel}
                            visibilityModel={columnVisibilityModel}
                            onVisibilityModelChange={handleColumnVisibilityModelChange}
                            componentId="trials-overview"
                        />
                    </Grid>
                </Grid>
            </Box>

            <Paper elevation={0}>
                <StyledDataGrid
                    rows={(metrics && metrics.data) || []}
                    rowCount={metrics && metrics.metadata ? metrics.metadata.total : 0}
                    columns={memoizedColumns}
                    columnGroupingModel={columnGroupingModel(metricsType)}
                    experimentalFeatures={{ columnGrouping: true }}
                    getRowId={row => row._id}
                    sortingMode={'server'}
                    sortModel={reportSort}
                    sortingOrder={['desc', 'asc', null]}
                    onSortModelChange={(model: GridSortModel) => {
                        setReportSort(model);
                    }}
                    filterMode="server"
                    // filterModel={filter}
                    pagination
                    paginationMode="server"
                    pageSizeOptions={[20]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model: GridPaginationModel) => {
                        setPaginationModel(model);
                    }}
                    autoHeight
                    disableColumnFilter
                    disableColumnMenu
                    disableRowSelectionOnClick
                    // Add column visibility model
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                    slots={{
                        noRowsOverlay: () => (
                            <Stack height="100%" alignItems="center" justifyContent="center">
                                No Results Found
                            </Stack>
                        ),
                    }}
                    initialState={{
                        pinnedColumns: {
                            // left: ['foreignId'],
                            right: [
                                'actions',
                                // 'edit', 'welcomeMessage', 'share'
                            ],
                        },
                        pagination: {
                            paginationModel: {
                                pageSize: 20,
                                page: 0,
                            },
                        },
                    }}
                    loading={isLoading}
                    sx={{
                        '& .boldHeader': {
                            fontWeight: 'bold',
                        },
                        '& .MuiDataGrid-cell': {
                            backgroundColor: theme.palette.common.white,
                        },
                        '& .MuiDataGrid-columnHeader': {
                            backgroundColor: theme.palette.common.white,
                        },
                    }}
                />
                {editMetric && editDialogOpen && (
                    <SextforceMetricsEditSettingsDialog
                        metricData={editMetric}
                        onClose={() => {
                            setEditDialogOpen(false);
                        }}
                        open={editDialogOpen}
                        saveSettings={saveSettings}
                        isSavingSettings={isSavingSettings}
                    />
                )}
                {welcomeMessageMetric && welcomeMessageDialogOpen && (
                    <SextforceMetricsTrialsWelcomeMessageDialog
                        subscriber={subscriber}
                        trial={welcomeMessageMetric}
                        open={welcomeMessageDialogOpen}
                        onClose={() => {
                            setWelcomeMessageDialogOpen(false);
                        }}
                    />
                )}
                {shareMetric && shareDialogOpen && (
                    <SextforceMetricsShareDialog
                        open={shareDialogOpen}
                        onClose={handleShareDialogClose}
                        subscriber={subscriber}
                        metricData={shareMetric}
                    />
                )}
                {claimedTodayDateDialogOpen && (
                    <SextforceMetricsClaimsTodayDateSelectorDialog
                        open={claimedTodayDateDialogOpen}
                        onClose={() => setClaimedTodayDateDialogOpen(false)}
                        date={claimedTodayDate}
                        setDate={setClaimedTodayDate}
                        timezone={timezone}
                        setTimezone={setTimezone}
                    />
                )}
            </Paper>
        </>
    );
};

export default SextforceMetricsTrialsOverviewGrid;
